/**
 * Crunch Design System — FileUpload
 *
 * Figma component: modal-file-upload (component set, node 3931:6044)
 *
 * Drop-zone container. Four states: default, drop-success, error, disabled.
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faFile,
    faFileExclamation,
    faGaugeSimpleMax,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

const DEFAULT_ICONS: Record<string, IconDefinition> = {
    default: faFile,
    "drop-success": faFile,
    error: faFileExclamation,
    disabled: faGaugeSimpleMax,
};

export function FileUpload({
    state = "default",
    title = "Drag and drop your file here",
    description,
    buttonLabel = "Choose files",
    onButtonClick,
    icon,
    onDrop,
    onDragOver,
    onDragEnter,
    onDragLeave,
    className,
    ref,
    ...rest
}: {
    state?: "default" | "drop-success" | "error" | "disabled";
    title?: string;
    description?: string;
    buttonLabel?: string;
    onButtonClick?: () => void;
    icon?: React.ReactNode;
    onDrop?: React.DragEventHandler<HTMLDivElement>;
    onDragOver?: React.DragEventHandler<HTMLDivElement>;
    onDragEnter?: React.DragEventHandler<HTMLDivElement>;
    onDragLeave?: React.DragEventHandler<HTMLDivElement>;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    const cls = [
        "cr-file-upload",
        state === "drop-success" && "cr-file-upload--drop-success",
        state === "error" && "cr-file-upload--error",
        state === "disabled" && "cr-file-upload--disabled",
        className,
    ].filter(Boolean).join(" ");

    return (
        <div
            className={cls}
            ref={ref}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            {...rest}
        >
            <div className="cr-empty-state">
                <div className="cr-empty-state__message">
                    <span className="cr-empty-state__icon">
                        {icon || <FontAwesomeIcon icon={DEFAULT_ICONS[state] || DEFAULT_ICONS.default} />}
                    </span>
                    <p className="cr-empty-state__title">{title}</p>
                    {description && (
                        <p className="cr-empty-state__description">{description}</p>
                    )}
                </div>
                <button
                    className="cr-btn cr-btn--secondary"
                    type="button"
                    disabled={state === "disabled"}
                    onClick={onButtonClick}
                >
                    <span className="cr-btn__label">{buttonLabel}</span>
                </button>
            </div>
        </div>
    );
}
